import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, LayoutGroup } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey.js';
import { visibleTabCount } from '../lib/nav-overflow.js';
import { cn } from '../lib/utils.js';

export interface PillNavTab {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

interface Props {
  tabs: PillNavTab[];
  onPrefetch?: (to: string) => void;
}

// Framer Motion approach: the active tab renders an absolutely-positioned
// background div with a shared `layoutId`. When the active tab changes,
// Framer Motion automatically animates the div between the old and new
// positions using FLIP (First-Last-Invert-Play). No manual measurement,
// no refs, no CSS transitions, no state.
//
// This replaces five iterations of in-house animation attempts that all
// failed in different ways (spring-from-x=0, no slide, snap-from-#1,
// computed-transition-none-despite-correct-state). Framer Motion is
// battle-tested for this exact use case.
//
// Overflow: every tab is also mounted in a hidden measuring row (same
// `.gi-pill-track` / `.gi-pill-tab` classes, so widths are identical). The
// measuring row keeps the stack's intrinsic width at "all tabs", so the
// stack's rendered width IS the available width; a ResizeObserver on it plus
// the per-tab widths feed `visibleTabCount`. Tabs that do not fit collapse,
// from the END of the list, into a trailing "More" pill with a dropdown
// (dropdown pattern copied from UserMenu.tsx). When everything fits the
// visible track is exactly the pre-overflow markup.
export function PillNav({ tabs, onPrefetch }: Props) {
  const { pathname } = useLocation();
  const stackRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(tabs.length);
  const [open, setOpen] = useState(false);

  const measure = useCallback(() => {
    const stack = stackRef.current;
    const row = measureRef.current;
    if (!stack || !row) return;
    const pills = Array.from(row.children) as HTMLElement[];
    if (pills.length === 0) return;
    const more = pills[pills.length - 1]!;
    const tabWidths = pills.slice(0, -1).map((el) => el.getBoundingClientRect().width);
    const cs = getComputedStyle(row);
    const chrome =
      parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) +
      parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth);
    const gap = parseFloat(cs.columnGap) || 0;
    const available = stack.getBoundingClientRect().width - chrome;
    const next = visibleTabCount({
      tabWidths,
      moreWidth: more.getBoundingClientRect().width,
      gap,
      available,
    });
    setVisibleCount((prev) => (prev === next ? prev : next));
  }, []);

  useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    if (stackRef.current) ro.observe(stackRef.current);
    if (measureRef.current) ro.observe(measureRef.current);
    return () => ro.disconnect();
  }, [measure, tabs]);

  // Outside click closes (UserMenu pattern: document mousedown on a ref).
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (stackRef.current && !stackRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const close = useCallback(() => setOpen(false), []);
  useEscapeKey(open, close);

  if (tabs.length < 2) return null;

  function isTabActive(tab: PillNavTab): boolean {
    if (tab.end) return pathname === tab.to;
    return pathname === tab.to || pathname.startsWith(tab.to + '/');
  }

  const count = Math.min(visibleCount, tabs.length);
  const shown = tabs.slice(0, count);
  const hidden = tabs.slice(count);
  const moreActive = hidden.some(isTabActive);

  return (
    <div className="gi-pill-switcher">
      <div ref={stackRef} className="gi-pill-stack">
        {/* Hidden measuring row: every tab + the More pill, never interactive. */}
        <div ref={measureRef} className="gi-pill-track gi-pill-measure" aria-hidden>
          {tabs.map((tab) => (
            <span key={tab.to} className="gi-pill-tab">
              <span className="gi-pill-tab-icon">
                <tab.icon className="h-3.5 w-3.5" />
              </span>
              <span>{tab.label}</span>
            </span>
          ))}
          <span className="gi-pill-tab">
            <span>More</span>
            <span className="gi-pill-tab-icon">
              <ChevronDown className="h-3.5 w-3.5" />
            </span>
          </span>
        </div>

        <div className="gi-pill-track">
          <LayoutGroup>
            {shown.map((tab) => {
              const active = isTabActive(tab);
              return (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  end={tab.end}
                  onMouseEnter={() => onPrefetch?.(tab.to)}
                  onFocus={() => onPrefetch?.(tab.to)}
                  className={`gi-pill-tab${active ? ' is-active' : ''}`}
                >
                  {active && (
                    <motion.div
                      layoutId="gi-pill-indicator"
                      className="gi-pill-indicator"
                      transition={{
                        type: 'spring',
                        stiffness: 380,
                        damping: 32,
                      }}
                      aria-hidden
                    />
                  )}
                  <span className="gi-pill-tab-icon" aria-hidden>
                    <tab.icon className="h-3.5 w-3.5" />
                  </span>
                  <span style={{ position: 'relative', zIndex: 1 }}>{tab.label}</span>
                </NavLink>
              );
            })}
            {hidden.length > 0 && (
              <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
                className={`gi-pill-tab gi-pill-more${moreActive ? ' is-active' : ''}`}
              >
                {moreActive && (
                  <motion.div
                    layoutId="gi-pill-indicator"
                    className="gi-pill-indicator"
                    transition={{
                      type: 'spring',
                      stiffness: 380,
                      damping: 32,
                    }}
                    aria-hidden
                  />
                )}
                <span style={{ position: 'relative', zIndex: 1 }}>More</span>
                <span className="gi-pill-tab-icon" aria-hidden>
                  <ChevronDown className="h-3.5 w-3.5" />
                </span>
              </button>
            )}
          </LayoutGroup>
        </div>

        {/* Dropdown lives outside the scrolling track so it is not clipped. */}
        {open && hidden.length > 0 && (
          <div
            role="menu"
            className="absolute right-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-md border border-border bg-card text-foreground shadow-xl"
          >
            {hidden.map((tab) => {
              const active = isTabActive(tab);
              return (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  end={tab.end}
                  role="menuitem"
                  aria-current={active ? 'page' : undefined}
                  onMouseEnter={() => onPrefetch?.(tab.to)}
                  onFocus={() => onPrefetch?.(tab.to)}
                  onClick={close}
                  className={cn(
                    'flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted',
                    active ? 'bg-muted font-semibold text-foreground' : 'text-muted-foreground',
                  )}
                >
                  <tab.icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span>{tab.label}</span>
                </NavLink>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
